/**
 * Typed BFF transport for this surface. Same-origin `/bff/v1/...` only (the browser never calls qm-api).
 * `fetch` is injectable so screens and api clients are testable offline.
 */
import type { ErrorEnvelope } from '@qm/contracts';
import { BffError } from '../../../lib/bff';

export { BffError };
export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;
export type Query = Record<string, string | number | boolean | null | undefined>;
export type ApiPath = `/v1/${string}`;
export interface Envelope<T> { data: T; meta?: Record<string, unknown> }

export interface Http {
  get<T>(path: ApiPath, query?: Query): Promise<T>;
  page<T>(path: ApiPath, query?: Query): Promise<Envelope<T>>;
  post<T>(path: ApiPath, body?: unknown, opts?: { idempotencyKey?: string }): Promise<T>;
  put<T>(path: ApiPath, body?: unknown): Promise<T>;
  patch<T>(path: ApiPath, body?: unknown): Promise<T>;
  del<T>(path: ApiPath, query?: Query): Promise<T>;
  /** Raw body (file upload): the body is sent as-is with this content type; the reply is still a JSON envelope. */
  send<T>(method: 'POST' | 'PUT', path: ApiPath, body: Blob | ArrayBuffer, contentType: string, query?: Query): Promise<T>;
}

export function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

/** Path segment encoder: ids from the URL are never concatenated raw. */
export const seg = (v: string) => encodeURIComponent(v);

export function newIdempotencyKey(): string {
  const c = globalThis.crypto as Crypto | undefined;
  return c?.randomUUID ? c.randomUUID() : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function createHttp(fetchImpl: FetchLike = (i, init) => fetch(i, init)): Http {
  async function call<T>(method: string, path: string, body?: unknown, headers: Record<string, string> = {}, raw?: { body: Blob | ArrayBuffer; contentType: string }): Promise<Envelope<T>> {
    const res = await fetchImpl(`/bff${path}`, raw ? {
      method,
      credentials: 'same-origin',
      headers: { accept: 'application/json', 'content-type': raw.contentType, ...headers },
      body: raw.body,
    } : {
      method,
      credentials: 'same-origin',
      headers: { accept: 'application/json', ...(body !== undefined ? { 'content-type': 'application/json' } : {}), ...headers },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const parsed = res.status === 204 ? null : await res.json().catch(() => null);
    if (!res.ok) throw new BffError(res.status, parsed as ErrorEnvelope | null);
    return (parsed ?? { data: null }) as Envelope<T>;
  }
  return {
    get: async (p, q) => (await call<never>('GET', withQuery(p, q))).data,
    page: (p, q) => call('GET', withQuery(p, q)),
    post: async (p, b, o) => (await call<never>('POST', p, b ?? {}, o?.idempotencyKey ? { 'idempotency-key': o.idempotencyKey } : {})).data,
    put: async (p, b) => (await call<never>('PUT', p, b ?? {})).data,
    patch: async (p, b) => (await call<never>('PATCH', p, b ?? {})).data,
    del: async (p, q) => (await call<never>('DELETE', withQuery(p, q))).data,
    send: async (m, p, b, ct, q) => (await call<never>(m, withQuery(p, q), undefined, {}, { body: b, contentType: ct })).data,
  };
}

/** Error code + reason from a failed call (for i18n mapping). */
export function errorInfo(err: unknown): { status: number; code: string; reason: string | null; correlationId: string | null } {
  if (err instanceof BffError) {
    const e = err.envelope?.error;
    const details = (e?.details ?? null) as { reason?: unknown } | null;
    return {
      status: err.status,
      code: e?.code ?? (err.status === 404 ? 'not_found' : 'error'),
      reason: typeof details?.reason === 'string' ? details.reason : null,
      correlationId: e?.correlationId ?? null,
    };
  }
  return { status: 0, code: 'network', reason: null, correlationId: null };
}

/** The error envelope's details object (reason, usedBy, problems, latestVersion, ...) or, for zod errors, the issue list. */
export function errorDetails(err: unknown): Record<string, unknown> | unknown[] | null {
  if (!(err instanceof BffError)) return null;
  const d = err.envelope?.error?.details as unknown;
  return d && typeof d === 'object' ? (d as Record<string, unknown> | unknown[]) : null;
}

/**
 * Staff access approval (flag identity.staff_access_approval): a 403 `access_approval_required` carries a path on this
 * console that opens "Request access" prefilled. Only a same-console `/access?...` path is ever used (no open redirect).
 */
export function accessRequestPath(err: unknown): string | null {
  if (!(err instanceof BffError) || err.envelope?.error?.code !== 'access_approval_required') return null;
  const p = (err.envelope.error.details as { requestPath?: unknown } | null | undefined)?.requestPath;
  return typeof p === 'string' && /^\/access\?[A-Za-z0-9_.~%=&-]*$/.test(p) ? p : '/access';
}
